import { useMemo } from 'react'
import styles from './ProducerSimulationBox.module.scss'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next/pages'
import { useRouter } from 'next/router'
import PromoCode from 'components/promo-code/PromoCode'
import { ProducerSimulationBoxProps } from './types'
import { PopulatedLocation } from 'components/producers/new-producer-map/types'
import { getCapacityState, isReserved } from 'utils/producers'
import { routes } from 'utils/routes'
import { setSelectedProducer } from 'store/app/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { InputGroupKeys } from 'store/customer/enums'
import { useGetLanguage } from 'hooks/useGetLanguage'
import Link from 'components/link/Link'
import { Button, List } from '@boltenergy-be/design-system'

const ProducerSimulationBox = ({ boxClass, highlighted, producer, sticky, openModal, hide, showPromoCode }: ProducerSimulationBoxProps) => {
  // Redux store
  const { didSimulation } = useStoreSelector((store) => store.customer.flows.simulation)
  const selectedProposition = useStoreSelector((store) => store.customer.propositions.selectedProposition)
  const simulation = useSelectInputGroup(InputGroupKeys.SIMULATION)
  const dispatch = useStoreDispatch()

  // Next router
  const router = useRouter()

  // i18n
  const { t } = useTranslation('producer')
  const language = useGetLanguage()

  // Constants
  const populatedLocation = useMemo<PopulatedLocation[]>(
    () =>
      producer.locations.map((location) => ({
        id: producer.id,
        name: producer.name,
        ...location
      })),
    [producer.id, producer.locations, producer.name]
  )
  const allLocationsSoldOut = useMemo<boolean>(
    () => populatedLocation.every((location) => getCapacityState(location, simulation, didSimulation).soldOut),
    [populatedLocation, simulation, didSimulation]
  )
  const allLocationsReserved = useMemo<boolean>(() => populatedLocation.every((location) => isReserved(location)), [populatedLocation])

  // Handles the click on the CTA
  const handleClick = () => {
    // If producer has multiple locations, open the modal
    if (producer.locations.length > 1) {
      openModal()
    } else {
      if (selectedProposition) {
        return router.push(routes(language, producer.slug).registration)
      } else {
        dispatch(setSelectedProducer({ producerSlug: producer.slug }))
        return router.push(routes(language).simulation)
      }
    }
  }

  return (
    <div className={classNames(styles.box, [boxClass], { [styles.highlighted]: highlighted, [styles.sticky]: sticky })}>
      {!hide?.title && <h3>{t(($) => $.simulationBox.buyFrom, { name: producer.name[language] })}</h3>}

      {/* PROMO CODE */}
      {showPromoCode && <PromoCode variant="side-card" />}

      {/* CTA */}
      {!hide?.cta && (
        <>
          {allLocationsSoldOut || allLocationsReserved ? (
            <Button disabled className={styles.cta}>
              {allLocationsSoldOut ? t(($) => $.status.soldOut) : t(($) => $.status.reserved)}
            </Button>
          ) : (
            <Button onClick={handleClick} className={styles.cta}>
              {t(($) => $.simulationBox.choseThisProducer)}
            </Button>
          )}
        </>
      )}

      {(allLocationsSoldOut || allLocationsReserved) && (
        <Link href={routes(language).producers} trailingIcon="chevronRight" className={styles['choose-different-btn']} size="small">
          {t(($) => $.chooseDifferentProducer)}
        </Link>
      )}

      {/* ADVANTAGES */}
      {!hide?.advantages && (
        <List as="ul" bullets="check">
          <li key="your-choice">{t(($) => $.advantage1)}</li>
          <li key="local">{t(($) => $.advantage2)}</li>
          <li key="durable">{t(($) => $.advantage3)}</li>
        </List>
      )}
    </div>
  )
}

export default ProducerSimulationBox
